import { useCallback, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import ProposalSettings, {
  CustomersDetailsNames,
} from 'src/db/entities/proposalSettings/ProposalSettingsEntity';
import { cloneSettingsForInvoice } from '../../mappers';

export type FormValues = {
  logoUrl?: string;
  businessName: string;
  description: string;
  phoneNumber: string;
  fax: string;
  email: string;
  address: string;
  website: string;
  moreDetails: string;
  bankName: string;
  bankAccountHolder: string;
  bankAccountNumber: string;
  bankSwift: string;
  paymentInstructions: string;
  taxRate: number;
  currency: string;
  includeTotalPrice: boolean;
  customerDetails: Array<'email' | 'fax' | 'address' | 'phone'>;
  footerText: string;
  includesTax: boolean;
  enteringPriceIncludesTax: boolean;
  startNumber: number;
  validity: number;
};

const mapEntityToFormValues = (settings: ProposalSettings): FormValues => ({
  logoUrl: settings.businessDetails.imageUrl ?? undefined,
  businessName: settings.businessDetails.name ?? '',
  description: settings.businessDetails.description ?? '',
  phoneNumber: settings.businessDetails.phone ?? '',
  fax: settings.businessDetails.fax ?? '',
  email: settings.businessDetails.email ?? '',
  address: settings.businessDetails.address ?? '',
  website: settings.businessDetails.website ?? '',
  moreDetails: settings.businessDetails.moreDetails ?? '',
  bankName: settings.businessDetails.bankName ?? '',
  bankAccountHolder: settings.businessDetails.bankAccountHolder ?? '',
  bankAccountNumber: settings.businessDetails.bankAccountNumber ?? '',
  bankSwift: settings.businessDetails.bankSwift ?? '',
  paymentInstructions: settings.businessDetails.paymentInstructions ?? '',
  taxRate: settings.taxRate,
  currency: settings.currency,
  includeTotalPrice: settings.includeTotalPrice,
  customerDetails: settings.customerDetails
    .filter((detail) => detail.enabled)
    .map((detail) => detail.name) as FormValues['customerDetails'],
  footerText: settings.footerText,
  includesTax: settings.includesTax,
  enteringPriceIncludesTax: settings.enteringPriceIncludesTax,
  startNumber: settings.startPrice ?? 0,
  validity: settings.validity ?? 30,
});

// A payment detail the settings never had stays unset while the field is empty, so the server
// keeps its stored value; once it has a value, clearing the field sends '' and clears it.
const paymentDetail = (stored: string | null | undefined, value: string) =>
  stored == null && !value ? stored : value ?? '';

const mapFormToEntity = (values: FormValues, base: ProposalSettings): ProposalSettings => ({
  ...base,
  taxRate: values.taxRate,
  currency: values.currency,
  includeTotalPrice: values.includeTotalPrice,
  includesTax: values.includesTax,
  enteringPriceIncludesTax: values.enteringPriceIncludesTax,
  lang: typeof window !== 'undefined' ? window.navigator.language : base.lang,
  startPrice: values.startNumber,
  validity: values.validity,
  footerText: values.footerText,
  customerDetails: base.customerDetails.map((detail) => ({
    ...detail,
    enabled: values.customerDetails.includes(detail.name as CustomersDetailsNames),
  })) as ProposalSettings['customerDetails'],
  businessDetails: {
    ...base.businessDetails,
    imageUrl: values.logoUrl ?? null,
    name: values.businessName ?? null,
    description: values.description ?? null,
    phone: values.phoneNumber ?? null,
    email: values.email ?? null,
    address: values.address ?? null,
    website: values.website ?? null,
    fax: values.fax ?? null,
    moreDetails: values.moreDetails ?? null,
    bankName: paymentDetail(base.businessDetails.bankName, values.bankName),
    bankAccountHolder: paymentDetail(
      base.businessDetails.bankAccountHolder,
      values.bankAccountHolder
    ),
    bankAccountNumber: paymentDetail(
      base.businessDetails.bankAccountNumber,
      values.bankAccountNumber
    ),
    bankSwift: paymentDetail(base.businessDetails.bankSwift, values.bankSwift),
    paymentInstructions: paymentDetail(
      base.businessDetails.paymentInstructions,
      values.paymentInstructions
    ),
  },
});

export default function useSettingsSubForm(initial?: ProposalSettings | null) {
  const schema = useMemo(
    () =>
      yup.object().shape({
        businessName: yup.string(),
        description: yup.string(),
        phoneNumber: yup.string(),
        fax: yup.lazy((value) =>
          !value ? yup.string() : yup.string().matches(/^[0-9 #*+-]+$/, 'Invalid fax number')
        ),
        email: yup.string().email('Invalid email'),
        address: yup.string(),
        website: yup.lazy((value) =>
          !value
            ? yup.string()
            : yup
                .string()
                .matches(
                  /((https?):\/\/)?(www.)?[a-z0-9]+(\.[a-z]{2,}){1,3}(#?\/?[a-zA-Z0-9#]+)*\/?(\?[a-zA-Z0-9-_]+=[a-zA-Z0-9-%]+&?)?$/,
                  'Invalid URL'
                )
        ),
        moreDetails: yup.string(),
        bankName: yup.string(),
        bankAccountHolder: yup.string(),
        bankAccountNumber: yup.string(),
        bankSwift: yup.string(),
        paymentInstructions: yup.string(),
        taxRate: yup.number().required().min(0).max(100),
        currency: yup.string().required(),
        includeTotalPrice: yup.boolean().required(),
        footerText: yup.string().max(500),
      }),
    []
  );

  const baseRef = useRef(cloneSettingsForInvoice(initial));
  const methods = useForm<FormValues>({
    defaultValues: mapEntityToFormValues(baseRef.current),
    resolver: yupResolver(schema),
    mode: 'onBlur',
  });
  const { reset } = methods;

  const resetWithSettings = useCallback(
    (settings?: ProposalSettings | null) => {
      const snapshot = cloneSettingsForInvoice(settings);
      baseRef.current = snapshot;
      reset(mapEntityToFormValues(snapshot));
    },
    [reset]
  );

  const toEntity = useCallback(
    (values: FormValues) => mapFormToEntity(values, baseRef.current),
    []
  );

  return {
    ...methods,
    resetWithSettings,
    toEntity,
  };
}
